import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContextNew';
import { chatAPI } from '../services/api';
import websocketService from '../services/websocketService';
import { playNotificationSound } from '../utils/notificationSound';

const ChatUnreadContext = createContext({
  unreadCount: 0,
  refreshUnreadCount: async () => {}
});

export function ChatUnreadProvider({ children }) {
  const { isGuest } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnreadCount = useCallback(async () => {
    if (isGuest) {
      setUnreadCount(0);
      return;
    }
    try {
      const res = await chatAPI.getUnreadCount();
      if (res.success && res.data) {
        const n = res.data.unreadCount;
        setUnreadCount(typeof n === 'number' ? n : 0);
      } else {
        setUnreadCount(0);
      }
    } catch {
      setUnreadCount(0);
    }
  }, [isGuest]);

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  useEffect(() => {
    const onFocus = () => refreshUnreadCount();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshUnreadCount]);

  useEffect(() => {
    window.refreshChatCount = refreshUnreadCount;
    return () => {
      delete window.refreshChatCount;
    };
  }, [refreshUnreadCount]);

  // Bump when realtime message arrives (other threads)
  useEffect(() => {
    if (isGuest) return undefined;
    const bump = () => {
      // App-wide chime on any incoming message (the throttle in the util collapses
      // duplicate socket-room deliveries of the same message).
      playNotificationSound();
      refreshUnreadCount();
    };
    websocketService.on('new_message', bump);
    return () => websocketService.off('new_message', bump);
  }, [isGuest, refreshUnreadCount]);

  const value = useMemo(
    () => ({ unreadCount, refreshUnreadCount }),
    [unreadCount, refreshUnreadCount]
  );

  return <ChatUnreadContext.Provider value={value}>{children}</ChatUnreadContext.Provider>;
}

export function useChatUnread() {
  return useContext(ChatUnreadContext);
}
